import { useEffect, useState } from 'react'
import { Loader2 } from 'lucide-react'
import { toast } from 'sonner'
import { AiSparkleBadge } from '@/components/app/ai/AiSparkle'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import AuthAxios from '@/lib/api/axios/AuthAxios'
import { ENDPOINT_BASE } from '@/config/endpoints'
import useLoginStore from '@/store/LoginStore'

// Calls POST /api/v1.0/e/{slug}/ai/extract-tasks on the backend AiController.
// Renders the resulting proposals. Task creation (turning proposals into real
// WorkItems) is a separate step coming next — for now this is a review-only
// surface so the user can validate extraction quality on real pages.

type TaskProposal = {
  title: string
  description?: string | null
  ownerHint?: string | null
  dueHint?: string | null
  priority?: string | null
  evidence?: string | null
}

type ExtractResponse = {
  workspaceId: string
  documentId: string
  documentTitle: string | null
  model: string
  proposals: Array<TaskProposal>
}

function getErrorMessage(err: any) {
  return (
    err?.response?.data?.detail ||
    err?.response?.data?.title ||
    err?.response?.data?.message ||
    err?.response?.data?.error ||
    err?.message ||
    'Extraction failed'
  )
}

export interface ExtractTasksDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  documentId: string
  pageTitle?: string
}

export function ExtractTasksDialog({
  open,
  onOpenChange,
  documentId,
  pageTitle,
}: ExtractTasksDialogProps) {
  const workspaceSlug = useLoginStore((s) => s.workspaceSlug)
  const [loading, setLoading] = useState(false)
  const [result, setResult] = useState<ExtractResponse | null>(null)

  // Auto-run extraction on open so the user doesn't need to click twice.
  // Re-run if a different page is opened in the same dialog instance.
  useEffect(() => {
    if (!open || !documentId || !workspaceSlug) return

    let cancelled = false
    setLoading(true)
    setResult(null)

    AuthAxios.post<ExtractResponse>(
      `${ENDPOINT_BASE}/e/${workspaceSlug}/ai/extract-tasks`,
      { documentId },
    )
      .then((res) => {
        if (cancelled) return
        setResult(res.data)
        if (res.data.proposals.length === 0) {
          toast.info('No action items detected on this page')
        }
      })
      .catch((err) => {
        if (cancelled) return
        toast.error(getErrorMessage(err))
        onOpenChange(false)
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })

    return () => {
      cancelled = true
    }
  }, [open, documentId, workspaceSlug, onOpenChange])

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2.5 text-base">
            <AiSparkleBadge />
            Extract tasks from this page
          </DialogTitle>
          <DialogDescription className="truncate">
            {pageTitle ? `Page: ${pageTitle}` : 'Reviewing AI-extracted action items'}
          </DialogDescription>
        </DialogHeader>

        {loading && (
          <div className="text-muted-foreground flex items-center gap-2 py-8 text-sm">
            <Loader2 className="size-4 animate-spin" />
            Asking the model for action items…
          </div>
        )}

        {!loading && result && result.proposals.length === 0 && (
          <div className="text-muted-foreground rounded-md border border-dashed p-4 text-sm">
            No action items detected. The page may be informational, or the items
            lacked clear ownership / intent.
          </div>
        )}

        {!loading && result && result.proposals.length > 0 && (
          <div className="max-h-[60vh] space-y-2 overflow-y-auto pr-1">
            {result.proposals.map((p, i) => (
              <div key={i} className="rounded-md border p-3">
                <div className="mb-1 flex items-start justify-between gap-2">
                  <span className="text-sm font-medium">{p.title}</span>
                  {p.priority && (
                    <Badge
                      variant={
                        p.priority === 'high'
                          ? 'destructive'
                          : p.priority === 'medium'
                            ? 'default'
                            : 'secondary'
                      }
                      className="shrink-0 capitalize"
                    >
                      {p.priority}
                    </Badge>
                  )}
                </div>

                {p.description && (
                  <p className="text-muted-foreground mb-2 text-xs leading-relaxed">
                    {p.description}
                  </p>
                )}

                <div className="mb-2 flex flex-wrap gap-1.5">
                  {p.ownerHint && (
                    <Badge variant="outline" className="text-xs">
                      owner: {p.ownerHint}
                    </Badge>
                  )}
                  {p.dueHint && (
                    <Badge variant="outline" className="text-xs">
                      due: {p.dueHint}
                    </Badge>
                  )}
                </div>

                {p.evidence && (
                  <blockquote className="text-muted-foreground border-l-2 pl-2 text-xs italic">
                    “{p.evidence}”
                  </blockquote>
                )}
              </div>
            ))}
          </div>
        )}

        <div className="flex items-center justify-end gap-2 pt-2">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Close
          </Button>
          <Button disabled title="Task creation lands next session">
            Create tasks (coming next)
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  )
}
